import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { InstructDialog } from "@/components/app/InstructDialog";
import { celebrateInstruction } from "@/lib/celebrate";
import {
  Calculator, House, CurrencyGbp, CaretDown, CaretUp,
  CheckCircle, Buildings, Bank, User, Receipt, ArrowRight,
  Gift, Wallet, Handshake, HouseLine, Briefcase, Gavel, Plus, X,
  HouseSimple,
} from "@phosphor-icons/react";

function formatCurrency(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
}

export default function QuotePage({ isReferrer = false, bespokeReferrer = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSolicitor = user?.role === "solicitor";
  const isEmbed = searchParams.get("embed") === "1";
  const initialRefName = isReferrer ? (searchParams.get("ref_name") || "") : "";
  // Both bespoke + legacy referrer tool can use ref_token for an unambiguous
  // referrer lookup. Bespoke mode REQUIRES it (page errors without it),
  // legacy mode treats it as optional — falling back to a name-match lookup
  // so existing solicitor-shared links keep working.
  const refToken = (bespokeReferrer || isReferrer) ? (searchParams.get("ref_token") || "") : "";
  const [form, setForm] = useState({
    client_name: "",
    // Split-name fields — added Feb 2026 so the public/bespoke quote
    // forms collect first name + surname separately. We still send
    // `client_name` (the concatenated form) to the backend so existing
    // records, PDFs, emails, and downstream case creation continue to
    // work unchanged. The split values are stashed on the quote doc
    // for future use.
    client_first_name: "",
    client_surname: "",
    client_email: "",
    client_phone: "",
    property_value: "",
    property_address: "",
    property_postcode: "",
    transaction_type: "buying",
    property_type: "freehold",
    // Sale-side property fields (only when transaction_type === "both")
    sale_property_value: "",
    sale_property_address: "",
    sale_property_postcode: "",
    sale_property_type: "freehold",
    mortgage: false,
    first_time_buyer: false,
    second_home: false,
    gifted_deposit: false,
    num_giftors: "1",
    htb_isa: false,
    num_htb_isa: "1",
    lifetime_isa: false,
    num_lifetime_isa: "1",
    shared_ownership: false,
    new_build: false,
    ltd_company: false,
    auction: false,
    right_to_buy: false,
    bridging_finance: false,
    commercial: false,
    // Sale-side supplements (apply for "selling" or sale leg of "both")
    sale_mortgage_redemption: false,
    sale_shared_ownership: false,
    sale_htb_loan_redemption: false,
    sale_ltd_company: false,
    sale_auction: false,
    // Universal BSA — applies on any transaction type
    bsa: false,
    bsa_sale: false,
    // Remortgage-side supplements
    remortgage_htb_loan: false,
    remortgage_existing_mortgage: false,
    // Transfer of equity supplements
    toe_existing_mortgage: false,
    toe_new_mortgage: false,
    toe_shared_ownership: false,
    toe_htb_loan: false,
    additional_notes: "",
    referrer_name: initialRefName,
    referral_fee: "",
    referral_fee_sale: "",
    // Per-quote selection of admin-defined custom fees / disbursements.
    // Each entry is the custom fee's uuid. Toggle buttons render below
    // the supplement section based on the catalog returned from
    // /quote-defaults filtered by transaction_type.
    custom_fee_ids: [],
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [additionalClients, setAdditionalClients] = useState([]);
  const [instructDialogOpen, setInstructDialogOpen] = useState(false);
  const [referrerInstructing, setReferrerInstructing] = useState(false);
  const [bespokeReferrerInfo, setBespokeReferrerInfo] = useState(null);
  const [bespokeError, setBespokeError] = useState("");
  // Firm-wide custom fees catalogue — fetched once from the public
  // /quote-defaults endpoint so we can render an opt-in toggle for each
  // matching custom fee on the quote form.
  const [customFeesCatalog, setCustomFeesCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/quote-defaults");
        if (!cancelled) setCustomFeesCatalog(data?.custom_fees || []);
      } catch {
        // Non-fatal — quote still generates correctly without the preview banner.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Per-referrer branding overrides (only applied when bespoke referrer has
  // `use_brand: true` AND a primary colour is set). All other surfaces (the
  // public quote tool, the legacy referrer tool) keep UKC defaults.
  const useBrand = !!(bespokeReferrer && bespokeReferrerInfo?.use_brand && bespokeReferrerInfo?.brand_primary);
  const PRIMARY = useBrand ? bespokeReferrerInfo.brand_primary : '#100f5d';
  const ACCENT = useBrand ? (bespokeReferrerInfo.brand_accent || bespokeReferrerInfo.brand_primary) : '#c87d3a';
  const TEXT_ON_PRIMARY = useBrand ? (bespokeReferrerInfo.brand_text_on_primary || '#ffffff') : '#ffffff';
  const LOGO_URL = useBrand && bespokeReferrerInfo.brand_logo_url ? bespokeReferrerInfo.brand_logo_url : '/logo.png';
  const LOGO_ALT = useBrand ? (bespokeReferrerInfo.company || bespokeReferrerInfo.name || 'Conveyancing Quote') : 'UKC Legal Solicitors';
  const PRIMARY_TINT = (PRIMARY && PRIMARY.length === 7) ? `${PRIMARY}14` : 'rgba(16,15,93,0.08)'; // 8% tint

  // Resolve the referrer record on mount.
  //   • Bespoke tool: REQUIRES ref_token; surfaces an error if missing/invalid.
  //   • Legacy referrer tool: ref_token is optional. If present we use it;
  //     otherwise, when the solicitor is signed in we name-match against
  //     /referrers so commission defaults still pre-fill.
  useEffect(() => {
    if (!bespokeReferrer && !isReferrer) return;
    let cancelled = false;
    (async () => {
      try {
        if (refToken) {
          const { data } = await API.get(`/referrer-dashboard/${refToken}`);
          if (cancelled) return;
          setBespokeReferrerInfo(data?.referrer || null);
          return;
        }
        // Legacy fallback: name-match — only works for signed-in solicitors
        // because /referrers is auth-protected.
        if (isReferrer && initialRefName && isSolicitor) {
          const { data } = await API.get("/referrers");
          if (cancelled) return;
          const needle = initialRefName.trim().toLowerCase();
          const match = (data || []).find((r) =>
            (r.name || "").trim().toLowerCase() === needle ||
            (r.company || "").trim().toLowerCase() === needle
          );
          if (match) setBespokeReferrerInfo(match);
        }
      } catch {
        if (!cancelled && bespokeReferrer) {
          setBespokeError("Invalid or expired referrer link. Please contact your solicitor.");
        }
        // Legacy mode failure is silent — manual referral_fee entry still works.
      }
    })();
    return () => { cancelled = true; };
  }, [bespokeReferrer, isReferrer, refToken, initialRefName, isSolicitor]);

  // Auto-fill referral fee inputs from the referrer's saved commission
  // defaults whenever we have a referrer record (bespoke OR legacy mode).
  // For "both" we fill both Purchase + Sale inputs; for single-leg
  // transactions we fill the single fee input.
  //
  // Refill rule (so transaction-type swaps work cleanly):
  //   * Empty input  → fill with current type's default.
  //   * Input value matches ANY of the referrer's other commission_* defaults
  //     → assume it's a stale auto-fill from a previous tx type, refill with
  //     the current type's default.
  //   * Anything else (custom value typed by the user) → leave alone.
  useEffect(() => {
    if (!bespokeReferrerInfo) return;
    const ov = bespokeReferrerInfo.fee_overrides || {};
    // Set of every numeric commission default — used to detect "looks like
    // a stale prefill" so we can swap it on tx-type change.
    const commissionDefaults = new Set(
      ["commission_buying", "commission_selling", "commission_both",
       "commission_purchase", "commission_sale",
       "commission_remortgage", "commission_transfer"]
        .map((k) => ov[k])
        .filter((v) => v != null)
        .map((v) => Number(v))
    );
    const isStaleDefault = (val) => {
      if (val == null || val === "") return false;
      const n = Number(val);
      return !Number.isNaN(n) && commissionDefaults.has(n);
    };

    setForm((prev) => {
      const next = { ...prev };
      if (prev.transaction_type === "both") {
        const purchaseDefault = ov.commission_purchase ?? ov.commission_both;
        const saleDefault = ov.commission_sale ?? ov.commission_selling;
        if ((!prev.referral_fee || isStaleDefault(prev.referral_fee)) && purchaseDefault != null) {
          next.referral_fee = String(purchaseDefault);
        }
        if ((!prev.referral_fee_sale || isStaleDefault(prev.referral_fee_sale)) && saleDefault != null) {
          next.referral_fee_sale = String(saleDefault);
        }
      } else {
        const def = ov[`commission_${prev.transaction_type}`];
        if ((!prev.referral_fee || isStaleDefault(prev.referral_fee)) && def != null) {
          next.referral_fee = String(def);
        }
        // Clear sale-leg input if we're not in "both" mode and the value
        // looks like a stale prefill from a previous "both" quote.
        if (prev.referral_fee_sale && isStaleDefault(prev.referral_fee_sale)) {
          next.referral_fee_sale = "";
        }
      }
      return next;
    });
  }, [bespokeReferrerInfo, form.transaction_type]);

  // Re-generate flow: ?prefill_quote_id=X loads the existing quote and seeds
  // every form field so the broker can tweak property value / transaction type
  // / flags and submit a new quote without retyping. The original quote stays
  // untouched (audit trail), and we'd ideally link the new one with
  // `parent_quote_id` (handled below in handleSubmit).
  const prefillQuoteId = searchParams.get("prefill_quote_id") || "";
  const [prefillError, setPrefillError] = useState("");
  useEffect(() => {
    if (!prefillQuoteId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/quotes/${prefillQuoteId}`);
        if (cancelled) return;
        // Bespoke flow: only allow prefill if the referrer in the URL token
        // owns the quote (id-or-name match). Public quote tool: anyone with
        // the quote id can already view it, so prefill is fine.
        if (bespokeReferrer && bespokeReferrerInfo) {
          const owns =
            data.referrer_id === bespokeReferrerInfo.id ||
            ((data.referrer_name || "").trim().toLowerCase()
              === (bespokeReferrerInfo.name || "").trim().toLowerCase());
          if (!owns) {
            setPrefillError("This quote belongs to another referrer.");
            return;
          }
        } else if (isReferrer) {
          // Legacy referrer tool — verify the ?ref_name= URL param matches the
          // quote's referrer_name. Stops broker A pre-filling broker B's quote
          // through a guessed quote id.
          const urlRefName = (searchParams.get("ref_name") || "").trim().toLowerCase();
          const quoteRefName = (data.referrer_name || "").trim().toLowerCase();
          if (urlRefName && quoteRefName && urlRefName !== quoteRefName) {
            setPrefillError("This quote belongs to another referrer.");
            return;
          }
        }
        // Prefer the split fields if the saved quote has them; otherwise
        // split the legacy `client_name` on the LAST space (so "Mary Anne
        // Smith" → "Mary Anne" / "Smith"). The composed `client_name`
        // stays whatever was saved so the rest of the form is unaffected.
        const _legacyName = (data.client_name || "").trim();
        const _lastSpace = _legacyName.lastIndexOf(" ");
        const _splitFirst = data.client_first_name
          || (_lastSpace > 0 ? _legacyName.slice(0, _lastSpace) : _legacyName);
        const _splitSurname = data.client_surname
          || (_lastSpace > 0 ? _legacyName.slice(_lastSpace + 1) : "");
        setForm((prev) => ({
          ...prev,
          client_name: data.client_name || "",
          client_first_name: _splitFirst,
          client_surname: _splitSurname,
          client_email: data.client_email || "",
          client_phone: data.client_phone || "",
          property_value: data.property_value ? String(data.property_value) : "",
          property_address: data.property_address || "",
          property_postcode: data.property_postcode || "",
          transaction_type: data.transaction_type || "buying",
          property_type: data.property_type || "freehold",
          mortgage: !!data.mortgage,
          first_time_buyer: !!data.first_time_buyer,
          second_home: !!data.second_home,
          gifted_deposit: !!data.gifted_deposit,
          num_giftors: data.num_giftors ? String(data.num_giftors) : "1",
          htb_isa: !!data.htb_isa,
          num_htb_isa: data.num_htb_isa ? String(data.num_htb_isa) : "1",
          lifetime_isa: !!data.lifetime_isa,
          num_lifetime_isa: data.num_lifetime_isa ? String(data.num_lifetime_isa) : "1",
          shared_ownership: !!data.shared_ownership,
          new_build: !!data.new_build,
          ltd_company: !!data.ltd_company,
          auction: !!data.auction,
          right_to_buy: !!data.right_to_buy,
          bridging_finance: !!data.bridging_finance,
          commercial: !!data.commercial,
          sale_property_value: data.sale_property_value ? String(data.sale_property_value) : "",
          sale_property_address: data.sale_property_address || "",
          sale_property_postcode: data.sale_property_postcode || "",
          sale_property_type: data.sale_property_type || "freehold",
          sale_mortgage_redemption: !!data.sale_mortgage_redemption,
          sale_shared_ownership: !!data.sale_shared_ownership,
          sale_htb_loan_redemption: !!data.sale_htb_loan_redemption,
          sale_ltd_company: !!data.sale_ltd_company,
          sale_auction: !!data.sale_auction,
          bsa: !!data.bsa,
          bsa_sale: !!data.bsa_sale,
          remortgage_htb_loan: !!data.remortgage_htb_loan,
          remortgage_existing_mortgage: !!data.remortgage_existing_mortgage,
          toe_existing_mortgage: !!data.toe_existing_mortgage,
          toe_new_mortgage: !!data.toe_new_mortgage,
          toe_shared_ownership: !!data.toe_shared_ownership,
          toe_htb_loan: !!data.toe_htb_loan,
          additional_notes: data.additional_notes || "",
          referrer_name: data.referrer_name || prev.referrer_name,
          referral_fee: data.referral_fee ? String(data.referral_fee) : "",
          referral_fee_sale: data.referral_fee_sale ? String(data.referral_fee_sale) : "",
        }));
        if (Array.isArray(data.additional_clients) && data.additional_clients.length > 0) {
          setAdditionalClients(data.additional_clients);
        }
        toast.success("Quote loaded — adjust any fields and re-generate");
      } catch {
        if (!cancelled) setPrefillError("Couldn't load the original quote. Start a fresh one if needed.");
      }
    })();
    return () => { cancelled = true; };
  }, [prefillQuoteId, bespokeReferrer, bespokeReferrerInfo]);

  // Referrers don't have JWT auth — they submit a "client-instruct" request, exactly
  // like a client opening the public quote link. The solicitor then converts it to a
  // case from their side.
  const handleReferrerInstruct = async () => {
    if (!quote || referrerInstructing) return;
    setReferrerInstructing(true);
    try {
      const { data } = await API.post(`/quotes/${quote.id}/client-instruct`, {
        name: quote.client_name,
        email: quote.client_email,
        phone: quote.client_phone,
      });
      setQuote(data);
      toast.success("Instruction request sent to the solicitor. You'll be notified when the case opens.");
      // Conversion moment — haptic buzz + brand confetti + scroll to the
      // "Instruction Received" badge so the client immediately sees success.
      celebrateInstruction("instructed-badge");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send instruction");
    } finally {
      setReferrerInstructing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.property_value) return;
    // Email + phone are both required so we can reach the lead. Same goes
    // for every additional client on a joint application.
    if (!form.client_first_name.trim() || !form.client_surname.trim()) {
      toast.error("First name and surname are both required");
      return;
    }
    if (!form.client_email.trim()) {
      toast.error("Email address is required");
      return;
    }
    if (!form.client_phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    for (let i = 0; i < additionalClients.length; i++) {
      const c = additionalClients[i];
      const _first = (c.first_name || "").trim();
      const _last = (c.surname || "").trim();
      if (!_first || !_last || !c.email.trim() || !c.phone.trim()) {
        toast.error(`Additional client ${i + 1} needs first name, surname, email and phone`);
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        client_name: form.client_name,
        // Send the split-name fields too so any future backend/PDF
        // logic can use them. The composed `client_name` above stays
        // authoritative for current code paths.
        client_first_name: form.client_first_name,
        client_surname: form.client_surname,
        client_email: form.client_email,
        client_phone: form.client_phone,
        property_value: parseFloat(form.property_value),
        property_address: form.property_address,
        property_postcode: form.property_postcode,
        transaction_type: form.transaction_type,
        property_type: form.property_type,
        mortgage: form.mortgage,
        first_time_buyer: form.first_time_buyer,
        second_home: form.second_home,
        gifted_deposit: form.gifted_deposit,
        num_giftors: form.gifted_deposit ? parseInt(form.num_giftors) || 1 : 0,
        htb_isa: form.htb_isa,
        num_htb_isa: form.htb_isa ? parseInt(form.num_htb_isa) || 1 : 0,
        lifetime_isa: form.lifetime_isa,
        num_lifetime_isa: form.lifetime_isa ? parseInt(form.num_lifetime_isa) || 1 : 0,
        shared_ownership: form.shared_ownership,
        new_build: form.new_build,
        ltd_company: form.ltd_company,
        auction: form.auction,
        right_to_buy: form.right_to_buy,
        bridging_finance: form.bridging_finance,
        commercial: form.commercial,
        // Sale-side fields (server stores them only when relevant)
        sale_property_value: form.transaction_type === "both" && form.sale_property_value ? parseFloat(form.sale_property_value) : null,
        sale_property_address: form.sale_property_address,
        sale_property_postcode: form.sale_property_postcode,
        sale_property_type: form.sale_property_type,
        sale_mortgage_redemption: form.sale_mortgage_redemption,
        sale_shared_ownership: form.sale_shared_ownership,
        sale_htb_loan_redemption: form.sale_htb_loan_redemption,
        sale_ltd_company: form.sale_ltd_company,
        sale_auction: form.sale_auction,
        bsa: form.bsa,
        bsa_sale: form.bsa_sale,
        remortgage_htb_loan: form.remortgage_htb_loan,
        remortgage_existing_mortgage: form.remortgage_existing_mortgage,
        toe_existing_mortgage: form.toe_existing_mortgage,
        toe_new_mortgage: form.toe_new_mortgage,
        toe_shared_ownership: form.toe_shared_ownership,
        toe_htb_loan: form.toe_htb_loan,
        custom_fee_ids: (customFeesCatalog || [])
          .filter((cf) => (form.custom_fee_ids || []).includes(cf.id) && (cf.transaction_types || []).includes(form.transaction_type))
          .map((cf) => cf.id),
        additional_clients: additionalClients
          .filter(c => c.name.trim())
          .map(c => ({ name: c.name.trim(), email: c.email.trim(), phone: c.phone.trim() })),
        additional_notes: form.additional_notes,
      };
      if (isReferrer && form.referrer_name.trim()) {
        payload.referrer_name = form.referrer_name;
        payload.referral_fee = form.referral_fee ? parseFloat(form.referral_fee) : 0;
        if (form.transaction_type === "both") {
          payload.referral_fee_sale = form.referral_fee_sale ? parseFloat(form.referral_fee_sale) : 0;
        }
      }
      // Bespoke-referrer mode posts to a separate endpoint that resolves the
      // referrer's saved fee overrides + auto-fills commission. Original
      // /quote/referrer endpoint is left untouched.
      const endpoint = bespokeReferrer ? "/referrer-quotes" : "/quotes";
      if (bespokeReferrer) {
        if (!refToken) { toast.error("Missing referrer token in URL"); setLoading(false); return; }
        payload.ref_token = refToken;
      }
      const { data } = await API.post(endpoint, payload);
      setQuote(data);
      toast.success("Quote generated");
      // Notify parent window when running inside an iframe embed
      if (isEmbed && window.parent !== window) {
        try {
          window.parent.postMessage({
            type: "ukc_quote_generated",
            quote_id: data.id,
            total: data.total,
            client_name: data.client_name,
          }, "*");
        } catch (_) { /* no-op */ }
      }
    } catch (err) {
      toast.error("Failed to generate quote");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = () => {
    if (!quote) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${quote.id}/pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleNewQuote = () => {
    setQuote(null);
    setForm({ ...form, client_name: "", client_first_name: "", client_surname: "", client_email: "", client_phone: "", property_value: "", additional_notes: "", referrer_name: initialRefName, referral_fee: "", referral_fee_sale: "" });
  };

  const handleCopyLink = () => {
    if (!quote) return;
    navigator.clipboard.writeText(`${window.location.origin}/quote/view/${quote.id}`);
    toast.success("Quote link copied to clipboard");
  };

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  // Render the matching custom-fee toggles inline at the end of each
  // existing supplements row. Same Toggle button look as Mortgage/FTB/BSA.
  // Returns an array of <button> elements (or empty) so it can be slotted
  // into any existing flex-wrap. The catalog filter ensures each item
  // only appears in the section matching its declared transaction_types.
  // In bespoke-referrer mode, the displayed £ amount uses the referrer's
  // saved override (if any) so brokers see their bespoke price — matching
  // what the backend will actually charge.
  const renderCustomFeeToggles = () => {
    const matched = (customFeesCatalog || []).filter((cf) => (cf.transaction_types || []).includes(form.transaction_type));
    if (matched.length === 0) return null;
    const cfOverrides = (bespokeReferrer && bespokeReferrerInfo?.fee_overrides?.custom_fees) || {};
    return matched.map((cf) => {
      const on = (form.custom_fee_ids || []).includes(cf.id);
      const isDisb = cf.category === "disbursement";
      const onTone = isDisb
        ? "bg-lime-50 border-lime-200 text-lime-700"
        : "bg-cyan-50 border-cyan-200 text-cyan-700";
      const override = cfOverrides[cf.id];
      const displayAmount = override != null ? Number(override) : cf.amount;
      const toggle = () => set(
        "custom_fee_ids",
        on
          ? (form.custom_fee_ids || []).filter((x) => x !== cf.id)
          : [...(form.custom_fee_ids || []), cf.id]
      );
      return (
        <button
          key={cf.id}
          type="button"
          onClick={toggle}
          data-testid={`custom-fee-toggle-${cf.id}`}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
            on ? onTone : "border-slate-200 text-slate-500 hover:border-slate-300"
          }`}
        >
          <Receipt weight={on ? "fill" : "regular"} className="w-3.5 h-3.5" />
          {cf.name}
          <span className="text-[10px] opacity-75">{formatCurrency(displayAmount)}</span>
          {on && <CheckCircle weight="fill" className="w-3 h-3" />}
        </button>
      );
    });
  };

  if (quote) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="quote-result-page">
        <div className="border-b print:hidden" style={{ background: PRIMARY }}>
          <div className="h-[3px]" style={{ background: ACCENT }} />
          <div className="max-w-3xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO_URL} alt={LOGO_ALT} className="h-10 w-auto max-w-[160px] object-contain shrink-0" onError={(e) => { e.currentTarget.src = '/logo.png'; }} />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif', color: TEXT_ON_PRIMARY }}>Your Conveyancing Quote</h1>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>Generated {new Date(quote.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 print:hidden">
              <Button variant="outline" onClick={handleCopyLink} data-testid="copy-quote-link-btn" className="h-8 text-xs bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white">
                Share Link
              </Button>
              <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn" className="h-8 text-xs bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white">
                Download PDF
              </Button>
              <Button variant="outline" onClick={handlePrint} data-testid="print-quote-btn" className="h-8 text-xs bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white">
                Print
              </Button>
              <Button onClick={handleNewQuote} data-testid="new-quote-btn" className="h-8 text-xs text-white" style={{ background: ACCENT }}>
                New Quote
              </Button>
              {isSolicitor && !quote.instructed && (
                <Button onClick={() => setInstructDialogOpen(true)} data-testid="instruct-btn"
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                  Instruct
                </Button>
              )}
              {!isSolicitor && !quote.instructed && !quote.client_instructed && (
                <Button onClick={handleReferrerInstruct} disabled={referrerInstructing} data-testid="public-instruct-btn"
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                  {referrerInstructing ? "Sending…" : "Request to Instruct"}
                </Button>
              )}
              {(quote.instructed || quote.client_instructed) && (
                <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${
                  quote.instructed ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                }`} data-testid="instructed-badge">
                  {quote.instructed ? "Case Opened" : "Instruction Received"}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8" id="quote-printable">
          {/* Client info */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 font-medium">Client</p>
                <p className="text-sm font-semibold text-slate-900">{quote.client_name}</p>
                {quote.client_email && <p className="text-xs text-slate-500">{quote.client_email}</p>}
                {quote.additional_clients?.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {quote.additional_clients.map((c, i) => (
                      <p key={i} className="text-xs text-slate-500">+ {c.name}{c.email ? ` (${c.email})` : ""}</p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">{quote.transaction_type === "both" ? "Purchase Property Value" : quote.transaction_type === "remortgage" ? "Loan Value" : quote.transaction_type === "grant_of_lease" ? "Lease Premium" : "Property Value"}</p>
                <p className="text-sm font-semibold text-slate-900">{formatCurrency(quote.property_value)}</p>
                {(quote.property_address || quote.property_postcode) && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {[quote.property_address, quote.property_postcode].filter(Boolean).join(", ")}
                  </p>
                )}
                {quote.transaction_type === "both" && (quote.sale_property_value || quote.sale_property_address) && (
                  <div className="mt-2 pt-2 border-t border-slate-100" data-testid="sale-property-summary">
                    <p className="text-xs text-slate-500 font-medium">Sale Property Value</p>
                    <p className="text-sm font-semibold text-slate-900">{quote.sale_property_value ? formatCurrency(quote.sale_property_value) : "—"}</p>
                    {(quote.sale_property_address || quote.sale_property_postcode) && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {[quote.sale_property_address, quote.sale_property_postcode].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Transaction</p>
                <p className="text-sm font-medium text-slate-700">{({ buying: "Buying", selling: "Selling", both: "Buying & Selling", remortgage: "Remortgage", transfer: "Transfer of Equity", grant_of_lease: "Grant of Lease" })[quote.transaction_type] || quote.transaction_type} · <span className="capitalize">{quote.property_type}</span></p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Details</p>
                <div className="flex gap-2 mt-0.5 flex-wrap">
                  {quote.mortgage && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Mortgage</span>}
                  {quote.first_time_buyer && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">First-time buyer</span>}
                  {quote.second_home && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700">Second Home / BTL</span>}
                  {quote.new_build && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">New Build</span>}
                  {quote.shared_ownership && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">Shared Ownership</span>}
                  {quote.gifted_deposit && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">Gifted Deposit ({quote.num_giftors} giftor{quote.num_giftors !== 1 ? "s" : ""})</span>}
                  {quote.htb_isa && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Help to Buy ISA ({quote.num_htb_isa || 1})</span>}
                  {quote.lifetime_isa && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-pink-50 text-pink-700">Lifetime ISA ({quote.num_lifetime_isa || 1})</span>}
                  {quote.ltd_company && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-white">Ltd Company</span>}
                  {quote.auction && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">Auction</span>}
                  {quote.right_to_buy && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Right to Buy</span>}
                  {quote.commercial && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-teal-50 text-teal-700">Commercial / Mixed-Use</span>}
                  {quote.sale_mortgage_redemption && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Mortgage Redemption</span>}
                  {quote.sale_shared_ownership && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">Shared Ownership (Sale)</span>}
                  {quote.sale_htb_loan_redemption && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">HTB Loan Redemption</span>}
                  {quote.sale_ltd_company && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-700 text-white">Ltd Co (Sale)</span>}
                  {quote.sale_auction && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">Auction (Sale)</span>}
                  {quote.bsa && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{quote.transaction_type === "both" ? "BSA (Purchase)" : "BSA"}</span>}
                  {quote.bsa_sale && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">BSA (Sale)</span>}
                  {quote.remortgage_htb_loan && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">HTB Loan</span>}
                  {quote.remortgage_existing_mortgage && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Existing Mortgage</span>}
                  {quote.toe_existing_mortgage && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Existing Mortgage</span>}
                  {quote.toe_new_mortgage && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">New Mortgage</span>}
                  {quote.toe_shared_ownership && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">Shared Ownership</span>}
                  {quote.toe_htb_loan && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">Existing HTB Loan</span>}
                  {!quote.mortgage && !quote.first_time_buyer && !quote.new_build && !quote.shared_ownership && !quote.gifted_deposit && !quote.htb_isa && !quote.lifetime_isa && !quote.ltd_company && !quote.auction && !quote.right_to_buy && !quote.commercial && !quote.sale_mortgage_redemption && !quote.sale_shared_ownership && !quote.sale_htb_loan_redemption && !quote.sale_ltd_company && !quote.sale_auction && !quote.bsa && !quote.bsa_sale && !quote.remortgage_htb_loan && !quote.remortgage_existing_mortgage && !quote.toe_existing_mortgage && !quote.toe_new_mortgage && !quote.toe_shared_ownership && !quote.toe_htb_loan && <span className="text-xs text-slate-400">-</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Quote breakdown */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-6 space-y-5">
            {(() => {
              // For "Buying & Selling" quotes, visually split the fees into
              // two mini-sections. Sale-side line items are tagged with "(Sale)"
              // in their names (see _append_sale_supplements in quotes.py).
              // Disbursements, SDLT and the non-"(Sale)" legal items belong
              // to the purchase leg.
              const isBoth = quote.transaction_type === "both";
              const allLegal = (quote.legal_items || [{ name: "Solicitor's fee", amount: quote.legal_fee }])
                // Drop any £0 line items so the displayed quote stays clean.
                .filter((it) => Number(it.amount || 0) > 0);
              const saleItems = isBoth ? allLegal.filter((it) => /\(Sale\)/i.test(it.name)) : [];
              const purchaseItems = isBoth ? allLegal.filter((it) => !/\(Sale\)/i.test(it.name)) : allLegal;
              const SECTIONS = isBoth
                ? [
                    { key: "purchase", title: "Purchase Fees", items: purchaseItems },
                    { key: "sale", title: "Sale Fees", items: saleItems },
                  ]
                : [{ key: "single", title: "Legal Fees", items: purchaseItems }];
              const subTotal = (items) => items.reduce((s, i) => s + (i.amount || 0), 0);
              const vatOf = (items) => Math.round(subTotal(items) * 0.2 * 100) / 100;

              return SECTIONS.map((sec, idx) => (
                <div key={sec.key}>
                  {idx > 0 && <hr className="border-slate-100 my-2" data-testid={`split-divider-${sec.key}`} />}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide" data-testid={`section-title-${sec.key}`}>{sec.title}</h3>
                      {isBoth && (
                        <span className="text-[10px] font-medium text-slate-400">{sec.items.length} item{sec.items.length === 1 ? "" : "s"}</span>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      {sec.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-600">{item.name.replace(/\s*\(Sale\)\s*$/i, "")}</span>
                          <span className="font-medium">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">VAT (20%)</span>
                        <span className="font-medium">{formatCurrency(isBoth ? vatOf(sec.items) : quote.vat_on_legal)}</span>
                      </div>
                      {isBoth && (
                        <div className="flex justify-between text-sm pt-1 border-t border-slate-100 mt-1">
                          <span className="font-semibold text-slate-800">Subtotal</span>
                          <span className="font-semibold text-slate-900" data-testid={`section-subtotal-${sec.key}`}>
                            {formatCurrency(subTotal(sec.items) + vatOf(sec.items))}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ));
            })()}

              <hr className="border-slate-100" />

              {/* Disbursements */}
              <div>
                <button onClick={() => setShowBreakdown(!showBreakdown)} className="flex items-center justify-between w-full mb-2">
                  <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide">Disbursements</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{formatCurrency(quote.disbursements_total)}</span>
                    {showBreakdown ? <CaretUp className="w-3 h-3 text-slate-400" /> : <CaretDown className="w-3 h-3 text-slate-400" />}
                  </div>
                </button>
                {showBreakdown && (
                  <div className="space-y-1.5 pl-2 border-l-2 border-slate-100 ml-1">
                    {quote.disbursements
                      .filter((d) => Number(d.amount || 0) > 0)
                      .map((d, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-slate-500">{d.name}</span>
                        <span className="text-slate-700">{formatCurrency(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* SDLT */}
              {quote.sdlt && quote.sdlt.total > 0 && (
                <>
                  <hr className="border-slate-100" />
                  <div>
                    <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-2">Stamp Duty (SDLT)</h3>
                    <div className="space-y-1.5 pl-2 border-l-2 border-slate-100 ml-1">
                      {quote.sdlt.bands.map((b, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-slate-500">{formatCurrency(b.from)} – {formatCurrency(b.to)} @ {b.rate}%</span>
                          <span className="text-slate-700">{formatCurrency(b.tax)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-sm font-medium mt-2">
                      <span className="text-slate-700">SDLT Total</span>
                      <span>{formatCurrency(quote.sdlt.total)}</span>
                    </div>
                    {quote.sdlt.first_time_buyer && (
                      <p className="text-[10px] text-emerald-600 font-medium mt-1">First-time buyer relief applied</p>
                    )}
                    {quote.sdlt.second_home && (
                      <p className="text-[10px] text-red-600 font-medium mt-1">5% second home / BTL surcharge applied</p>
                    )}
                    {!quote.sdlt.second_home && quote.sdlt.ltd_company && (
                      <p className="text-[10px] text-red-600 font-medium mt-1">5% higher rate (Ltd company purchase) applied</p>
                    )}
                  </div>
                </>
              )}
              {quote.sdlt && quote.sdlt.total === 0 && quote.transaction_type !== "selling" && (
                <>
                  <hr className="border-slate-100" />
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Stamp Duty (SDLT)</span>
                    <span className="font-medium text-emerald-600">{formatCurrency(0)} {quote.first_time_buyer ? "(FTB relief)" : ""}</span>
                  </div>
                </>
              )}
            </div>

            {/* Total */}
            <div className="text-white p-6" style={{ background: PRIMARY }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide font-medium" style={{ color: ACCENT }}>Estimated Total</p>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>Including VAT, disbursements{quote.sdlt?.total > 0 ? " & SDLT" : ""}</p>
                </div>
                <p className="text-3xl font-bold" data-testid="quote-total" style={{ fontFamily: 'Outfit, sans-serif', color: TEXT_ON_PRIMARY }}>{formatCurrency(quote.total)}</p>
              </div>
            </div>
          </div>

          {quote.additional_notes && (
            <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm mb-6">
              <p className="text-xs font-semibold text-slate-900 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-sm text-slate-600">{quote.additional_notes}</p>
            </div>
          )}

          <p className="text-center text-[10px] text-slate-400 mt-8">
            This quote is an estimate and may vary. Disbursements are approximate and subject to third-party charges. Quote ref: {quote.id.slice(0, 8).toUpperCase()}
          </p>
          {/* Spacer so the sticky mobile CTA below doesn't cover the footer line */}
          <div className="sm:hidden h-20" aria-hidden />
        </div>

        {/* Sticky mobile CTA — keeps the Instruct call-to-action visible while
            the client scrolls through the long disbursements / SDLT breakdown
            on a phone. Hidden on ≥sm because the inline header buttons are
            already visible there. Respects iOS safe-area inset. */}
        {!quote.instructed && !quote.client_instructed && (
          <div
            className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3 print:hidden"
            style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="mobile-sticky-cta"
          >
            {isSolicitor ? (
              <Button
                onClick={() => setInstructDialogOpen(true)}
                data-testid="mobile-sticky-instruct-btn"
                className="w-full h-11 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Instruct · {formatCurrency(quote.total)}
              </Button>
            ) : (
              <Button
                onClick={handleReferrerInstruct}
                disabled={referrerInstructing}
                data-testid="mobile-sticky-request-instruct-btn"
                className="w-full h-11 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {referrerInstructing ? "Sending…" : `Request to Instruct · ${formatCurrency(quote.total)}`}
              </Button>
            )}
          </div>
        )}

        {/* Instruct Dialog */}
        {quote && (
          <InstructDialog open={instructDialogOpen} onOpenChange={setInstructDialogOpen}
            quoteId={quote.id} quoteName={quote.client_name} />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-form-page">
      {!isEmbed && (
        <>
          {/* Brand band */}
          <div style={{ background: PRIMARY }}>
            <div className="h-[3px]" style={{ background: ACCENT }} />
            <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
              <img src={LOGO_URL} alt={LOGO_ALT} className="h-12 w-auto max-w-[200px] object-contain" onError={(e) => { e.currentTarget.src = '/logo.png'; }} />
              {!useBrand && (
                <div className="hidden sm:block text-right">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#e89e63' }}>Norwich</p>
                  <a href="tel:01603339174" className="text-sm text-white hover:underline" style={{ fontFamily: 'Manrope, sans-serif' }}>01603 339174</a>
                </div>
              )}
            </div>
          </div>

          {/* Tagline hero */}
          <div className="bg-white border-b border-slate-200">
            <div className="max-w-3xl mx-auto px-4 py-8 text-center">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full" style={{ background: PRIMARY_TINT }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: PRIMARY }}>
                  {bespokeReferrer ? 'Bespoke Quote Tool' : isReferrer ? 'Referrer Quote Tool' : 'Instant Online Quote'}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: PRIMARY }}>
                {bespokeReferrer
                  ? (bespokeReferrerInfo
                      ? `${bespokeReferrerInfo.company || bespokeReferrerInfo.name} · Bespoke Quote Tool`
                      : 'Bespoke Quote Tool')
                  : isReferrer ? 'Generate a quote with your referral fee included' : 'Fast and affordable conveyancing'}
              </h1>
              {prefillQuoteId && !prefillError && (
                <div className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-medium px-2.5 py-1 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }} data-testid="prefill-banner">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Re-generating from a previous quote — tweak any field and submit
                </div>
              )}
              {prefillError && (
                <div className="inline-flex items-center gap-1.5 mt-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-rose-50 text-rose-700">
                  {prefillError}
                </div>
              )}
              <p className="text-sm text-slate-500 max-w-xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {bespokeReferrer
                  ? 'Generate an instant fixed-fee quote.'
                  : isReferrer
                  ? 'Get an instant fixed-fee quote.'
                  : 'Get an instant fixed-fee quote — transparent, no hidden charges. Average completion 4–10 weeks.'}
              </p>
              {bespokeReferrer && bespokeError && (
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold bg-red-50 text-red-700 border border-red-200" data-testid="bespoke-error">
                  {bespokeError}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="max-w-2xl mx-auto px-4 py-8">
        {bespokeReferrer && bespokeError ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center space-y-3" data-testid="bespoke-invalid-link">
            <p className="text-sm font-semibold text-slate-900">This referrer link is no longer valid</p>
            <p className="text-xs text-slate-500">Please contact your solicitor to receive an up-to-date link.</p>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
          {/* Referrer info — hidden in bespoke mode (referrer + commission auto-resolved from token) */}
          {isReferrer && !bespokeReferrer && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-5 space-y-4">
              <h2 className="text-sm font-semibold text-violet-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <User weight="bold" className="w-4 h-4" /> Referrer Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-violet-700">Referrer / Agency Name</Label>
                  <Input data-testid="referrer-name-input" value={form.referrer_name} onChange={(e) => set("referrer_name", e.target.value)}
                    placeholder="e.g., ABC Estates" required className="bg-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-violet-700">{form.transaction_type === "both" ? "Referral Fee — Purchase (excl. VAT)" : "Referral Fee (excl. VAT)"}</Label>
                  <div className="relative">
                    <CurrencyGbp weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-violet-400" />
                    <Input data-testid="referral-fee-input" type="number" min="0" step="0.01" value={form.referral_fee}
                      onChange={(e) => set("referral_fee", e.target.value)} placeholder="0.00" className="pl-8 bg-white" />
                  </div>
                  {(() => {
                    // Show "Default for X" hint when the input still matches
                    // the bespoke-rate prefill — disappears the moment the
                    // user types over it. Built locally so no extra state.
                    const ov = bespokeReferrerInfo?.fee_overrides || {};
                    const txType = form.transaction_type;
                    const defaultFee = txType === "both"
                      ? (ov.commission_purchase ?? ov.commission_both)
                      : ov[`commission_${txType}`];
                    const txLabel = {
                      buying: "Buying", selling: "Selling",
                      remortgage: "Remortgage", transfer: "Transfer of Equity",
                      both: "Buying & Selling", grant_of_lease: "Grant of Lease",
                    }[txType] || txType;
                    const matchesDefault = defaultFee != null
                      && form.referral_fee !== ""
                      && Number(form.referral_fee) === Number(defaultFee);
                    if (!matchesDefault) return null;
                    return (
                      <p className="text-[10px] text-violet-600/80" data-testid="referral-fee-default-hint">
                        Default for {txLabel} — edit to override
                      </p>
                    );
                  })()}
                </div>
              </div>
              {form.transaction_type === "both" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="hidden sm:block" />
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-violet-700">Referral Fee — Sale (excl. VAT)</Label>
                    <div className="relative">
                      <CurrencyGbp weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-violet-400" />
                      <Input data-testid="referral-fee-sale-input" type="number" min="0" step="0.01" value={form.referral_fee_sale}
                        onChange={(e) => set("referral_fee_sale", e.target.value)} placeholder="0.00" className="pl-8 bg-white" />
                    </div>
                    {(() => {
                      const ov = bespokeReferrerInfo?.fee_overrides || {};
                      const defaultSale = ov.commission_sale ?? ov.commission_selling;
                      const matchesDefault = defaultSale != null
                        && form.referral_fee_sale !== ""
                        && Number(form.referral_fee_sale) === Number(defaultSale);
                      return matchesDefault ? (
                        <p className="text-[10px] text-violet-600/80" data-testid="referral-fee-sale-default-hint">
                          Default for Sale — edit to override
                        </p>
                      ) : (
                        <p className="text-[10px] text-violet-600/80">Entered separately from the Purchase fee. Each case file earns its own fee on completion.</p>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Client info */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <User weight="bold" className="w-4 h-4 text-slate-500" /> Client Details
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">First name *</Label>
                <Input
                  data-testid="client-first-name-input"
                  value={form.client_first_name}
                  onChange={(e) => {
                    const first = e.target.value;
                    // Keep the legacy `client_name` field in sync so the
                    // rest of the form (submit, prefill, draft auto-save)
                    // doesn't need to branch on the split fields.
                    const composed = `${first} ${form.client_surname || ""}`.trim();
                    setForm((f) => ({ ...f, client_first_name: first, client_name: composed }));
                  }}
                  placeholder="First name"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Surname *</Label>
                <Input
                  data-testid="client-surname-input"
                  value={form.client_surname}
                  onChange={(e) => {
                    const surname = e.target.value;
                    const composed = `${form.client_first_name || ""} ${surname}`.trim();
                    setForm((f) => ({ ...f, client_surname: surname, client_name: composed }));
                  }}
                  placeholder="Surname"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Email <span className="text-rose-500">*</span>
                </Label>
                <Input data-testid="client-email-input" type="email" required value={form.client_email}
                  onChange={(e) => set("client_email", e.target.value)} placeholder="email@example.com" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Phone <span className="text-rose-500">*</span>
                </Label>
                <Input data-testid="client-phone-input" type="tel" required value={form.client_phone}
                  onChange={(e) => set("client_phone", e.target.value)} placeholder="+44..." />
              </div>
            </div>
            {/* Additional clients — email + phone are required so the firm
                can reach every joint applicant. */}
            {additionalClients.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-slate-100">
                {additionalClients.map((c, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end" data-testid={`additional-client-${i}`}>
                    <div className="sm:col-span-1">
                      <Input placeholder="First name *" required value={c.first_name || ""} onChange={(e) => {
                        const first = e.target.value;
                        const updated = [...additionalClients];
                        // Keep `name` in sync as the concatenation so backend
                        // & validation that read `name` keep working.
                        updated[i] = { ...c, first_name: first, name: `${first} ${c.surname || ""}`.trim() };
                        setAdditionalClients(updated);
                      }} className="h-8 text-xs" data-testid={`additional-client-${i}-first-name`} />
                    </div>
                    <div className="sm:col-span-1">
                      <Input placeholder="Surname *" required value={c.surname || ""} onChange={(e) => {
                        const surname = e.target.value;
                        const updated = [...additionalClients];
                        updated[i] = { ...c, surname, name: `${c.first_name || ""} ${surname}`.trim() };
                        setAdditionalClients(updated);
                      }} className="h-8 text-xs" data-testid={`additional-client-${i}-surname`} />
                    </div>
                    <div className="sm:col-span-1">
                      <Input placeholder="Email *" type="email" required value={c.email} onChange={(e) => {
                        const updated = [...additionalClients]; updated[i] = { ...c, email: e.target.value }; setAdditionalClients(updated);
                      }} className="h-8 text-xs" data-testid={`additional-client-${i}-email`} />
                    </div>
                    <div className="sm:col-span-1">
                      <Input placeholder="Phone *" type="tel" required value={c.phone} onChange={(e) => {
                        const updated = [...additionalClients]; updated[i] = { ...c, phone: e.target.value }; setAdditionalClients(updated);
                      }} className="h-8 text-xs" data-testid={`additional-client-${i}-phone`} />
                    </div>
                    <button type="button" onClick={() => setAdditionalClients(additionalClients.filter((_, j) => j !== i))}
                      className="w-7 h-7 flex items-center justify-center rounded hover:bg-red-100 text-red-400 hover:text-red-600 shrink-0">
                      <X weight="bold" className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button type="button" onClick={() => setAdditionalClients([...additionalClients, { name: "", first_name: "", surname: "", email: "", phone: "" }])}
              data-testid="add-client-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 pt-1">
              <Plus weight="bold" className="w-3 h-3" /> Add another client
            </button>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <House weight="bold" className="w-4 h-4 text-slate-500" /> Property Details
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{form.transaction_type === "remortgage" ? "Loan Value *" : "Property Value *"}</Label>
                <div className="relative">
                  <CurrencyGbp weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input data-testid="property-value-input" type="number" min="1" step="1" value={form.property_value}
                    onChange={(e) => set("property_value", e.target.value)} placeholder="250,000" className="pl-8" required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Transaction Type</Label>
                <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type", v)}>
                  <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buying">Buying</SelectItem>
                    <SelectItem value="selling">Selling</SelectItem>
                    <SelectItem value="both">Buying & Selling</SelectItem>
                    <SelectItem value="remortgage">Remortgage</SelectItem>
                    <SelectItem value="transfer">Transfer of Equity</SelectItem>
                    <SelectItem value="grant_of_lease">Grant of Lease</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Property Type</Label>
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freehold">Freehold</SelectItem>
                    <SelectItem value="leasehold">Leasehold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Property Address</Label>
                <Input data-testid="property-address-input" value={form.property_address}
                  onChange={(e) => set("property_address", e.target.value)} placeholder="e.g., 10 High Street, London" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Postcode</Label>
                <Input data-testid="property-postcode-input" value={form.property_postcode}
                  onChange={(e) => set("property_postcode", e.target.value.toUpperCase())} placeholder="e.g., SW1A 1AA" className="uppercase" />
              </div>
            </div>

            {form.transaction_type !== "selling" && form.transaction_type !== "transfer" && form.transaction_type !== "remortgage" && (
            <div className="flex flex-wrap gap-2.5 pt-2">
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("mortgage", !form.mortgage)} data-testid="mortgage-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.mortgage ? "bg-blue-50 border-blue-200 text-blue-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Bank weight={form.mortgage ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Mortgage
                  {form.mortgage && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("first_time_buyer", !form.first_time_buyer)} data-testid="ftb-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.first_time_buyer ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Buildings weight={form.first_time_buyer ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  First-time buyer
                  {form.first_time_buyer && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("second_home", !form.second_home)} data-testid="second-home-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.second_home ? "bg-red-50 border-red-200 text-red-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <HouseSimple weight={form.second_home ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Second Home / BTL
                  {form.second_home && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("new_build", !form.new_build)} data-testid="new-build-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.new_build ? "bg-amber-50 border-amber-200 text-amber-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <HouseLine weight={form.new_build ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  New Build
                  {form.new_build && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              <button type="button" onClick={() => set("shared_ownership", !form.shared_ownership)} data-testid="shared-ownership-toggle"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  form.shared_ownership ? "bg-cyan-50 border-cyan-200 text-cyan-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}>
                <Handshake weight={form.shared_ownership ? "fill" : "regular"} className="w-3.5 h-3.5" />
                Shared Ownership
                {form.shared_ownership && <CheckCircle weight="fill" className="w-3 h-3" />}
              </button>
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("gifted_deposit", !form.gifted_deposit)} data-testid="gifted-deposit-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.gifted_deposit ? "bg-violet-50 border-violet-200 text-violet-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Gift weight={form.gifted_deposit ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Gifted Deposit
                  {form.gifted_deposit && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("htb_isa", !form.htb_isa)} data-testid="htb-isa-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.htb_isa ? "bg-rose-50 border-rose-200 text-rose-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Wallet weight={form.htb_isa ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Help to Buy ISA
                  {form.htb_isa && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("lifetime_isa", !form.lifetime_isa)} data-testid="lifetime-isa-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.lifetime_isa ? "bg-pink-50 border-pink-200 text-pink-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Wallet weight={form.lifetime_isa ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Lifetime ISA
                  {form.lifetime_isa && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              <button type="button" onClick={() => set("ltd_company", !form.ltd_company)} data-testid="ltd-company-toggle"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  form.ltd_company ? "bg-slate-800 border-slate-700 text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}>
                <Briefcase weight={form.ltd_company ? "fill" : "regular"} className="w-3.5 h-3.5" />
                Ltd Company
                {form.ltd_company && <CheckCircle weight="fill" className="w-3 h-3" />}
              </button>
              {form.transaction_type !== "remortgage" && (
                <button type="button" onClick={() => set("auction", !form.auction)} data-testid="auction-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.auction ? "bg-orange-50 border-orange-200 text-orange-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Gavel weight={form.auction ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Auction
                  {form.auction && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {/* Right to Buy — buying-only supplement (council /
                  housing-association sale at a discount). Shows for
                  Buying and Buying+Selling transactions; hidden for
                  remortgage/sale/grant-of-lease where it doesn't apply. */}
              {["buying", "both"].includes(form.transaction_type) && (
                <button type="button" onClick={() => set("right_to_buy", !form.right_to_buy)} data-testid="right-to-buy-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.right_to_buy ? "bg-rose-50 border-rose-200 text-rose-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <House weight={form.right_to_buy ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Right to Buy
                  {form.right_to_buy && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {/* Bridging finance — buying-only supplement. Bridge lender
                  liaison + expedited timeline + undertakings + exit
                  strategy paperwork. Hidden for remortgage / sale /
                  grant-of-lease where it doesn't apply. */}
              {["buying", "both"].includes(form.transaction_type) && (
                <button type="button" onClick={() => set("bridging_finance", !form.bridging_finance)} data-testid="bridging-finance-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.bridging_finance ? "bg-cyan-50 border-cyan-200 text-cyan-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <CurrencyGbp weight={form.bridging_finance ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Bridging Finance
                  {form.bridging_finance && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
              )}
              {/* Commercial element / mixed-use — adds a VAT-bearing legal
                  fee uplift AND flips SDLT to HMRC's non-residential
                  schedule (0% / 2% / 5%). Applies to all transaction
                  types where SDLT matters. */}
              <button type="button" onClick={() => set("commercial", !form.commercial)} data-testid="commercial-toggle"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  form.commercial ? "bg-teal-50 border-teal-200 text-teal-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}>
                <Buildings weight={form.commercial ? "fill" : "regular"} className="w-3.5 h-3.5" />
                Commercial Element
                {form.commercial && <CheckCircle weight="fill" className="w-3 h-3" />}
              </button>
              <button type="button" onClick={() => set("bsa", !form.bsa)} data-testid="bsa-toggle"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  form.bsa ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}>
                <Buildings weight={form.bsa ? "fill" : "regular"} className="w-3.5 h-3.5" />
                BSA
                {form.bsa && <CheckCircle weight="fill" className="w-3 h-3" />}
              </button>
              {renderCustomFeeToggles()}
            </div>
            )}

            {/* Conditional number inputs */}
            {(form.gifted_deposit || form.htb_isa || form.lifetime_isa) && (
              <div className="flex flex-wrap gap-4 pt-2 pl-1">
                {form.gifted_deposit && (
                  <div>
                    <Label className="text-xs font-medium">Number of Giftors</Label>
                    <Input data-testid="num-giftors-input" type="number" min="1" max="10" value={form.num_giftors}
                      onChange={(e) => set("num_giftors", e.target.value)} className="w-28 h-8 text-xs mt-1" />
                  </div>
                )}
                {form.htb_isa && (
                  <div>
                    <Label className="text-xs font-medium">Number of HTB ISAs</Label>
                    <Input data-testid="num-htb-isa-input" type="number" min="1" max="10" value={form.num_htb_isa}
                      onChange={(e) => set("num_htb_isa", e.target.value)} className="w-28 h-8 text-xs mt-1" />
                  </div>
                )}
                {form.lifetime_isa && (
                  <div>
                    <Label className="text-xs font-medium">Number of LISAs</Label>
                    <Input data-testid="num-lifetime-isa-input" type="number" min="1" max="10" value={form.num_lifetime_isa}
                      onChange={(e) => set("num_lifetime_isa", e.target.value)} className="w-28 h-8 text-xs mt-1" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sale property details — only when transaction includes a sale alongside a purchase */}
          {form.transaction_type === "both" && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4 shadow-sm" data-testid="sale-property-section">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <House weight="bold" className="w-4 h-4 text-slate-500" /> Sale Property Details
              </h2>
              <p className="text-[11px] text-slate-500 -mt-2">When instructed, this opens a separate Sale case file alongside the Purchase.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Sale Property Value</Label>
                  <div className="relative">
                    <CurrencyGbp weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input data-testid="sale-property-value-input" type="number" min="0" step="1" value={form.sale_property_value}
                      onChange={(e) => set("sale_property_value", e.target.value)} placeholder="200,000" className="pl-8" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Sale Property Type</Label>
                  <Select value={form.sale_property_type} onValueChange={(v) => set("sale_property_type", v)}>
                    <SelectTrigger data-testid="sale-property-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freehold">Freehold</SelectItem>
                      <SelectItem value="leasehold">Leasehold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Sale Property Address</Label>
                  <Input data-testid="sale-property-address-input" value={form.sale_property_address}
                    onChange={(e) => set("sale_property_address", e.target.value)} placeholder="e.g., 12 Old Lane, Norwich" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Sale Postcode</Label>
                  <Input data-testid="sale-property-postcode-input" value={form.sale_property_postcode}
                    onChange={(e) => set("sale_property_postcode", e.target.value.toUpperCase())} placeholder="e.g., NR1 1AA" className="uppercase" />
                </div>
              </div>
            </div>
          )}

          {/* Sale-side supplements — apply for "selling" or the sale leg of "both" */}
          {(form.transaction_type === "selling" || form.transaction_type === "both") && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3 shadow-sm" data-testid="sale-supplements-section">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Receipt weight="bold" className="w-4 h-4 text-slate-500" />
                {form.transaction_type === "both" ? "Sale-side Supplements" : "Supplements"}
              </h2>
              <div className="flex flex-wrap gap-2.5">
                <button type="button" onClick={() => set("sale_mortgage_redemption", !form.sale_mortgage_redemption)} data-testid="sale-mortgage-redemption-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.sale_mortgage_redemption ? "bg-blue-50 border-blue-200 text-blue-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Bank weight={form.sale_mortgage_redemption ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Mortgage Redemption
                  {form.sale_mortgage_redemption && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("sale_shared_ownership", !form.sale_shared_ownership)} data-testid="sale-shared-ownership-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.sale_shared_ownership ? "bg-cyan-50 border-cyan-200 text-cyan-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Handshake weight={form.sale_shared_ownership ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Shared Ownership
                  {form.sale_shared_ownership && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("sale_htb_loan_redemption", !form.sale_htb_loan_redemption)} data-testid="sale-htb-loan-redemption-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.sale_htb_loan_redemption ? "bg-rose-50 border-rose-200 text-rose-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Wallet weight={form.sale_htb_loan_redemption ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Help to Buy Loan
                  {form.sale_htb_loan_redemption && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("sale_ltd_company", !form.sale_ltd_company)} data-testid="sale-ltd-company-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.sale_ltd_company ? "bg-slate-800 border-slate-700 text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Briefcase weight={form.sale_ltd_company ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Ltd Company
                  {form.sale_ltd_company && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("sale_auction", !form.sale_auction)} data-testid="sale-auction-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.sale_auction ? "bg-orange-50 border-orange-200 text-orange-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Gavel weight={form.sale_auction ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Auction
                  {form.sale_auction && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                {/* Sale-side BSA toggle — on "both" this binds to bsa_sale (a separate
                    flag from the purchase-side BSA); on pure "selling" it binds to the
                    single `bsa` flag. */}
                {form.transaction_type === "selling" && (
                  <button type="button" onClick={() => set("bsa", !form.bsa)} data-testid="bsa-toggle"
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      form.bsa ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}>
                    <Buildings weight={form.bsa ? "fill" : "regular"} className="w-3.5 h-3.5" />
                    BSA
                    {form.bsa && <CheckCircle weight="fill" className="w-3 h-3" />}
                  </button>
                )}
                {form.transaction_type === "both" && (
                  <button type="button" onClick={() => set("bsa_sale", !form.bsa_sale)} data-testid="bsa-sale-toggle"
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      form.bsa_sale ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}>
                    <Buildings weight={form.bsa_sale ? "fill" : "regular"} className="w-3.5 h-3.5" />
                    BSA
                    {form.bsa_sale && <CheckCircle weight="fill" className="w-3 h-3" />}
                  </button>
                )}
                {form.transaction_type === "selling" && renderCustomFeeToggles()}
              </div>
            </div>
          )}

          {/* Remortgage-only supplements */}
          {form.transaction_type === "remortgage" && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3 shadow-sm" data-testid="remortgage-supplements-section">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Receipt weight="bold" className="w-4 h-4 text-slate-500" /> Supplements
              </h2>
              <div className="flex flex-wrap gap-2.5">
                <button type="button" onClick={() => set("remortgage_htb_loan", !form.remortgage_htb_loan)} data-testid="remortgage-htb-loan-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.remortgage_htb_loan ? "bg-rose-50 border-rose-200 text-rose-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Wallet weight={form.remortgage_htb_loan ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Help to Buy Loan
                  {form.remortgage_htb_loan && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("remortgage_existing_mortgage", !form.remortgage_existing_mortgage)} data-testid="remortgage-existing-mortgage-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.remortgage_existing_mortgage ? "bg-blue-50 border-blue-200 text-blue-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Bank weight={form.remortgage_existing_mortgage ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Existing Mortgage
                  {form.remortgage_existing_mortgage && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("bsa", !form.bsa)} data-testid="bsa-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.bsa ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Buildings weight={form.bsa ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  BSA
                  {form.bsa && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                {renderCustomFeeToggles()}
              </div>
            </div>
          )}

          {/* Transfer of equity supplements */}
          {form.transaction_type === "transfer" && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3 shadow-sm" data-testid="toe-supplements-section">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Receipt weight="bold" className="w-4 h-4 text-slate-500" /> Supplements
              </h2>
              <div className="flex flex-wrap gap-2.5">
                <button type="button" onClick={() => set("toe_existing_mortgage", !form.toe_existing_mortgage)} data-testid="toe-existing-mortgage-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.toe_existing_mortgage ? "bg-blue-50 border-blue-200 text-blue-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Bank weight={form.toe_existing_mortgage ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Existing Mortgage
                  {form.toe_existing_mortgage && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("toe_new_mortgage", !form.toe_new_mortgage)} data-testid="toe-new-mortgage-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.toe_new_mortgage ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Bank weight={form.toe_new_mortgage ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  New Mortgage
                  {form.toe_new_mortgage && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("toe_shared_ownership", !form.toe_shared_ownership)} data-testid="toe-shared-ownership-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.toe_shared_ownership ? "bg-cyan-50 border-cyan-200 text-cyan-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Handshake weight={form.toe_shared_ownership ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Shared Ownership
                  {form.toe_shared_ownership && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("toe_htb_loan", !form.toe_htb_loan)} data-testid="toe-htb-loan-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.toe_htb_loan ? "bg-rose-50 border-rose-200 text-rose-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Wallet weight={form.toe_htb_loan ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  Existing HTB Loan
                  {form.toe_htb_loan && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                <button type="button" onClick={() => set("bsa", !form.bsa)} data-testid="bsa-toggle"
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                    form.bsa ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}>
                  <Buildings weight={form.bsa ? "fill" : "regular"} className="w-3.5 h-3.5" />
                  BSA
                  {form.bsa && <CheckCircle weight="fill" className="w-3 h-3" />}
                </button>
                {renderCustomFeeToggles()}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-2">
            <Label className="text-xs font-medium">Additional Notes (optional)</Label>
            <Textarea data-testid="notes-input" value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)}
              placeholder="Any special requirements or notes..." rows={2} />
          </div>

          <Button type="submit" disabled={loading || !form.client_name.trim() || !form.property_value}
            data-testid="generate-quote-btn"
            className="w-full h-12 text-base text-white"
            style={{ background: PRIMARY }}>
            {loading ? "Generating..." : (
              <span className="flex items-center gap-2">
                <Calculator weight="bold" className="w-5 h-5" /> Generate Quote
              </span>
            )}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
